import { Domain } from '@betterfunnel/types';
import Loader from '@betterfunnel/ui-core/Loader';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
  deleteDomain as deleteDomainApi,
  refreshDomainStatus,
} from '../../services/DomainsService';
import ConfiguredSection from './ConfiguredSection';

interface Props {
  domainId: string;
  domain: Domain;
}

const DomainCard = ({ domainId, domain }: Props) => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);

  const [isValidating, setIsValidating] = useState(false);

  const deleteDomain = useCallback(async () => {
    setRemoving(true);
    try {
      await deleteDomainApi(selectedAccount?.id ?? '', domainId);
      show('Domain deleted');
    } catch (e: any) {
      Sentry.captureException(e);
      show('Unable to delete domain ' + e.message);
    } finally {
      setRemoving(false);
    }
  }, [selectedAccount?.id, domainId, show]);

  const getDomainStatus = useCallback(() => {
    if (isValidating || !selectedAccount) return;
    setIsValidating(true);

    refreshDomainStatus(selectedAccount.id, domainId).finally(() => {
      setIsValidating(false);
    });
  }, [selectedAccount, domainId, isValidating]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (domain.type !== 'custom' || domain.status !== 'pending') return;
    getDomainStatus();
  }, [domain.status, domain.type, getDomainStatus, selectedAccount]);

  const [removing, setRemoving] = useState(false);
  return (
    <div className="mt-10 w-full border-y border-black py-10 sm:rounded-lg sm:border sm:border-gray-50 sm:shadow-md">
      <div className="flex justify-between space-x-4 px-2 sm:px-10">
        <a
          href={`http://${domain.domain}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center text-left text-xl font-semibold"
        >
          {domain.domain}
          <span className="ml-2 inline-block">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              shapeRendering="geometricPrecision"
            >
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
            </svg>
          </span>
        </a>
        <div className="flex space-x-3">
          {domain.type === 'custom' ? (
            <button
              onClick={() => {
                getDomainStatus();
              }}
              disabled={isValidating}
              className={`${
                isValidating
                  ? 'cursor-not-allowed bg-gray-100'
                  : 'bg-white hover:border-black hover:text-black'
              } w-24 rounded-md border border-solid border-gray-200 py-1.5 text-sm text-gray-500 transition-all duration-150 ease-in-out focus:outline-hidden`}
            >
              {isValidating ? <Loader /> : 'Refresh'}
            </button>
          ) : (
            <></>
          )}
          {domain.type === 'custom' ? (
            <button
              onClick={deleteDomain}
              disabled={removing}
              className={`${
                removing ? 'cursor-not-allowed bg-gray-100' : ''
              }bg-red-500 w-24 rounded-md border border-solid border-red-500 py-1.5 text-sm text-white transition-all duration-150 ease-in-out hover:bg-white hover:text-red-500 focus:outline-hidden`}
            >
              {removing ? <Loader /> : 'Remove'}
            </button>
          ) : (
            <></>
          )}
        </div>
      </div>

      <ConfiguredSection domain={domain} />
    </div>
  );
};

export default DomainCard;
